import React from "react";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { startCase } from "lodash";
import ViewInfo from "components/reusable/ViewInfo";

const PayrollView = ({ setPayrollView, currentItem }) => {
  const { t } = useTranslation();

  // Helper to format currency
  const formatCurrency = (val) => `$${parseFloat(val || 0).toFixed(2)}`;
  const formatHours = (val) => `${parseFloat(val || 0).toFixed(1)} hrs`;

  return (
    <>
      <div className="justify-center items-center flex overflow-x-hidden overflow-y-auto fixed inset-0 z-50 outline-none focus:outline-none">
        <div className="relative w-auto my-6 mx-auto max-w-3xl">
          <form className="sm:py-4 sm:px-2 py-8 px-7">
            <div className="border-0 rounded-lg shadow-lg relative flex flex-col w-full min-w-[800px] max-h-[96vh] overflow-y-auto bg-white outline-none focus:outline-none">
              
              {/* Header */}
              <div className="flex items-center justify-between p-5 border-b border-solid border-slate-200 rounded-t">
                <h3 className="text-xl font-semibold">Payroll Details</h3>
                <button className="ml-auto flex items-center justify-center text-3xl text-black border-2 rounded-full h-8 w-8 float-right leading-none font-extralight outline-none" onClick={() => setPayrollView(false)}>
                  <span className="text-[#B8BBBF] text-4xl">×</span>
                </button>
              </div>
              
              <div className="relative p-6 flex-auto">
                
                {/* Section 1: Employee Details */}
                <div className="mb-6">
                  <h4 className="font-semibold text-md mb-3 border-b pb-2 text-gray-700">Employee Details</h4>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <p className="text-xs text-gray-500 uppercase font-bold">Name</p>
                      <p className="font-medium">{currentItem?.employee?.name || "N/A"}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 uppercase font-bold">Employee ID</p>
                      <p className="font-medium">{currentItem?.employee?.employeeId || "N/A"}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 uppercase font-bold">Pay Rate</p>
                      <p className="font-medium">{formatCurrency(currentItem?.hourlyRate)}/hr</p>
                    </div>
                  </div>
                </div>

                {/* Section 2: Pay Period */}
                <div className="mb-6">
                  <h4 className="font-semibold text-md mb-3 border-b pb-2 text-gray-700">Pay Period</h4>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <p className="text-xs text-gray-500 uppercase font-bold">Period Start</p>
                      <p className="font-medium">{currentItem?.payPeriod?.startDate ? dayjs(currentItem.payPeriod.startDate).format("MMM D, YYYY") : "N/A"}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 uppercase font-bold">Period End</p>
                      <p className="font-medium">{currentItem?.payPeriod?.endDate ? dayjs(currentItem.payPeriod.endDate).format("MMM D, YYYY") : "N/A"}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 uppercase font-bold">Frequency</p>
                      <p className="font-medium capitalize">{currentItem?.payFrequency || "N/A"}</p>
                    </div>
                  </div>
                </div>

                {/* Section 3: Wage Calculation */}
                <div className="mb-6">
                  <h4 className="font-semibold text-md mb-3 border-b pb-2 text-gray-700">Wage Calculation</h4>
                  <div className="grid grid-cols-2 gap-6">
                    
                    {/* Left Column: Hours */}
                    <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                      <p className="text-xs text-gray-500 uppercase font-bold mb-2">Hours Worked</p>
                      <div className="flex justify-between border-b pb-2 mb-2">
                        <span>Regular</span>
                        <span className="font-bold">{formatHours(currentItem?.regularHours)}</span>
                      </div>
                      <div className="flex justify-between border-b pb-2 mb-2">
                        <span>Overtime</span>
                        <span className="font-bold">{formatHours(currentItem?.overtimeHours)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Double Time</span>
                        <span className="font-bold">{formatHours(currentItem?.doubleTimeHours)}</span>
                      </div>
                    </div>

                    {/* Right Column: Earnings */}
                    <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                      <p className="text-xs text-blue-600 uppercase font-bold mb-2">Earnings</p>
                      <div className="flex justify-between border-b border-blue-200 pb-2 mb-2">
                        <span>Regular Pay</span>
                        <span className="font-bold">{formatCurrency(currentItem?.regularPay)}</span>
                      </div>
                      <div className="flex justify-between border-b border-blue-200 pb-2 mb-2">
                        <span>Overtime Pay</span>
                        <span className="font-bold">{formatCurrency(currentItem?.overtimePay)}</span>
                      </div>
                      <div className="flex justify-between border-b border-blue-200 pb-2 mb-2">
                        <span>Double Time Pay</span>
                        <span className="font-bold">{formatCurrency(currentItem?.doubleTimePay)}</span>
                      </div>
                      <div className="flex justify-between pt-2 border-t-2 border-blue-300 mt-2">
                        <span className="font-bold">Gross Pay</span>
                        <span className="font-bold text-blue-700">{formatCurrency(currentItem?.grossPay)}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Section 4: Net Pay & Status */}
                <div className="grid grid-cols-2 gap-6">
                  <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                    <p className="text-xs text-red-500 uppercase font-bold">Deductions</p>
                    <div className="flex justify-between mt-2">
                      <span>Tax Withheld</span>
                      <span className="font-bold">{formatCurrency(currentItem?.taxWithheld)}</span>
                    </div>
                  </div>
                  
                  <div className="p-4 bg-green-50 rounded-lg border border-green-200 flex flex-col justify-center items-center">
                    <p className="text-xs text-green-600 uppercase font-bold">Net Pay</p>
                    <p className="text-3xl font-bold text-green-700 mt-1">{formatCurrency(currentItem?.netPay)}</p>
                    <span className={`mt-2 px-3 py-1 rounded-full text-xs font-bold ${
                      currentItem?.status === 'completed' ? 'bg-green-200 text-green-800' :
                      currentItem?.status === 'processing' ? 'bg-blue-200 text-blue-800' :
                      currentItem?.status === 'failed' ? 'bg-red-200 text-red-800' :
                      'bg-yellow-200 text-yellow-800'
                    }`}>
                      {startCase(currentItem?.status)}
                    </span>
                  </div>
                </div>

              </div>

              {/* Footer */}
              <div className="border-t border-solid border-slate-200 rounded-b flex items-center justify-center p-6">
                <button onClick={() => setPayrollView(false)} className="text-black bg-[#E1E1E1] font-normal px-12 py-2.5 text-sm outline-none focus:outline-none rounded" type="button">
                  Close
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
      <div className="opacity-25 fixed inset-0 z-40 bg-black" />
    </>
  );
};

export default PayrollView;